import type { TranscriptLine } from './converse.ts';
import type { SessionEvent, SessionMeta, User } from './events.ts';
import type { JourneyStage, UserMemory } from './memory.ts';
import type { FlagLevel, ResolvedLevel } from './plan/flags.ts';
import type { CallPlan } from './plan/types.ts';
import type { Cefr } from './scoring.ts';

// Response shapes of /api/*. Request bodies are validated with zod on the server.
export type ConfigResponse = {
  authMode: 'google' | 'dev';
  googleClientId: string | null;
  liveModel: string;
  sha: string;
};
// `flags`: the feature flags as they apply to this user (src/server/plan/flags.ts), for UI a flag gates. `sandbox`: an
// admin is on their test account (ADR-0014), and `user.id` is that account's.
/** On admin writes: the account the tab shows, `test` or `own`, so a stale tab can't write to the other one. */
export const ACCOUNT_HEADER = 'x-fuse-account';
export type MeResponse = {
  user: User | null;
  isAdmin: boolean;
  flags: Record<string, 'on' | 'off'>;
  sandbox: boolean;
};
export type CreateLiveSessionResponse = {
  sessionId: string;
  model: string;
  maxMinutes: number;
  token?: string; // absent for the scripted transport
  expiresAt?: string;
  plan?: CallPlan; // what the call is for; absent when renewing a token
};
export type SessionEventsResponse = { meta: SessionMeta; events: SessionEvent[] };
export type ToolResponse = { result: Record<string, unknown> } | { error: string };
export type MemoryResponse = { memory: UserMemory };
/**
 * POST /api/sessions/:id/complete: memory after the call completed, or, when the completion is still running (a slow
 * updater), memory as it is and `updater: 'pending'`; the client reads memory until the call counts as completed.
 */
export type CompleteResponse = MemoryResponse & { updater?: 'pending' };

/** One change on the admin page (ADR-0014): the level before and after, as users got it. Kept for good. */
export type FeatureAudit = {
  name: string;
  from: ResolvedLevel;
  to: ResolvedLevel;
  reason: string;
  at: string;
  by: { id: string; email: string };
};
/** A declared flag on the admin page: the level in force, where it came from, and the cdk.json default. */
export type FlagView = ResolvedLevel & { name: string; default: FlagLevel; unreadable?: true };
/** GET /api/admin/features: the modules in the order they're asked (goals, then support), and the stored version. */
export type AdminFeaturesResponse = {
  version: number;
  /** The stored levels couldn't be read: every flag is off until they can. */
  unreadable: boolean;
  modules: Array<{ id: string; kind: 'goal' | 'support'; about: string; flag: FlagView | null }>;
  /** Levels in the table for flags this build doesn't declare: ignored, and clearable. */
  leftovers: Array<{ name: string; level: string }>;
};
export type AdminAuditResponse = { items: FeatureAudit[] };
/** The memory a preview plans from: the Lab's at a stage (reached today), the account in use, or pasted JSON. */
export type AdminPreviewRequest = {
  source: { kind: 'lab'; stage: JourneyStage } | { kind: 'account' } | { kind: 'json'; memory: unknown };
  stage?: JourneyStage;
  flags: Record<string, 'on' | 'off'>;
  days: number;
};
/** Preview's note when the goal would prepare at call start, which Preview never does (ADR-0014). */
export const PREVIEW_PREPARE_NOTE =
  'This call would start a check-in round, which writes its passage when the call starts (from the resume when there is one). Preview calls no model, so it shows the fixed passage: if the real call writes one, its prompt and hash differ.';
/** POST /api/admin/preview (ADR-0014): what a call would get, without calling a model or writing anything. */
export type AdminPreviewResponse = {
  /** The moment the call was planned for. */
  at: string;
  /** Each goal in the order it's asked, and whether it claims the call; the first that does is the plan's `main`. */
  claims: Array<{ id: string; claims: boolean }>;
  plan: CallPlan;
  prompt: string;
  promptChars: number;
  /** As the plan log computes it; it matches a logged call's only for the same name, memory, flags, date and build. */
  promptSha: string;
  /** When the goal does work at call start that Preview skips. */
  note?: string;
};

/** GET /api/admin/usage (ADR-0014): real users' calls from the plan log, as totals. */
export type AdminUsageResponse = {
  days: number;
  /** Why there are no numbers: no access to the logs here, or a query that failed. */
  unavailable?: string;
  since?: string;
  until?: string;
  /**
   * Per goal: calls planned; completed; completed with a user turn; with a speaking task on screen (logged since the
   * admin page's Usage shipped); with an open_tab call (which can be refused).
   */
  goals?: Array<{
    goal: string;
    planned: number;
    calls: number;
    used: number;
    opened: number;
    /** Completed calls that logged whether a task opened (older lines don't): the share of `opened` is of these. */
    openedKnown: number;
    openTab: number;
  }>;
  /** Check-in calls, and at call end: the round finished, it said "not today", the round had a result. */
  checkIn?: { calls: number; finished: number; notToday: number; withResult: number };
  /** Answers by kind (every scoring outcome: fleet, fuse's cap, fleet unreachable), and how many were scored. */
  scoring?: Array<{ kind: string; answers: number; scored: number }>;
};

/** Test runs (ADR-0015): caps, as the server enforces them and the page shows them. */
export const RUN_LIMITS = {
  perAdmin: 5,
  overall: 20,
  turns: 12,
  minutes: 5,
  personaChars: 2000,
  openingChars: 500,
} as const;
/**
 * `done`: the turns ran out, or the candidate was done. `cut`: the time cap stopped it. `stalled`: the model stopped
 * answering. `failed`: an error. `interrupted`: the server stopped (a deploy, say) before it finished.
 */
export const RUN_STATUSES = ['running', 'done', 'cut', 'stalled', 'failed', 'interrupted'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
/** POST /api/admin/runs: a memory as Preview takes it, flags and a date, and the simulated candidate. */
export type AdminRunRequest = AdminPreviewRequest & {
  persona: string;
  opening: string;
  turns: number;
  /** The level each unscripted answer counts as scored at; null for none, so no level shows. */
  cefr: Cefr | null;
};
/** One change a run made to memory: `before` absent when added, `after` absent when removed. Values as short JSON. */
export type MemoryChange = { path: string; before?: string; after?: string };
export type TestRun = {
  id: string;
  /** The admin who ran it (their own id, even from the test account). */
  by: { id: string; email: string };
  /** What it ran with. A pasted memory isn't kept, only that it was pasted. */
  input: Omit<AdminRunRequest, 'source'> & {
    source: { kind: 'lab'; stage: JourneyStage } | { kind: 'account' | 'json' };
  };
  status: RunStatus;
  error?: string;
  startedAt: string;
  updatedAt: string;
  /** How long it ran, once it ended. */
  ms?: number;
  plan?: CallPlan;
  promptSha?: string;
  transcript: TranscriptLine[];
  /** The tools the model called, in order. */
  tools: string[];
  /** What a real call would have left in memory, once the run completed the call. */
  changes: MemoryChange[];
  stage?: { before: JourneyStage; after: JourneyStage };
};
export type TestRunSummary = Omit<TestRun, 'transcript' | 'changes'>;
export type AdminRunsResponse = { runs: TestRunSummary[] };
export type AdminRunResponse = { run: TestRun };
/** Starting a run: the run, or why it was refused (busy, a cap reached, no Gemini key). */
export type AdminRunStartResponse = { run: TestRun } | { error: string };
